import {
    useContext,
    useEffect,
    useState,
    Fragment,
    useRef
} from "react";
import type {
    ReactNode
} from "react";
import {
    useWindowDimensions,
    Platform,
    Image,
    View
} from "react-native";
import type RootStackParamList from "./type";
import type {
    MenuButton
} from "./type";
import stylesheet from "./stylesheet";
import {
    NCoreUIKitEmbeddedMenu,
    NCoreUIKitLocalize,
    NCoreUIKitTheme,
    NCoreUIKitMenu,
    UserShortcut,
    MainHeader,
    SiteLogo,
    Button,
    Text
} from "ncore-ui-kit";
import {
    NavigationContainer,
    type LinkingOptions,
    getStateFromPath,
    useNavigation
} from "@react-navigation/native";
import {
    ClipboardListIcon,
    CalendarDaysIcon,
    MegaphoneIcon,
    NewspaperIcon,
    HardDriveIcon,
    Building2Icon,
    SettingsIcon,
    TerminalIcon,
    FileTextIcon,
    NetworkIcon,
    UsersIcon,
    InboxIcon,
    HomeIcon,
    BellIcon,
    UserIcon,
    MenuIcon
} from "lucide-react-native";
import {
    type NativeStackNavigationProp,
    createNativeStackNavigator
} from "@react-navigation/native-stack";
import AsyncStorage from "@react-native-async-storage/async-storage";
import {
    AuthContext
} from "../contexts/AuthContext";
import SettingsNewsCategories from "../pages/settings/views/newsCategories";
import SettingsSystemLocales from "../pages/settings/views/systemLocales";
import AnnouncementCreate from "../pages/announcements/views/create";
import AnnouncementDetail from "../pages/announcements/views/detail";
import SubEventCreate from "../pages/events/views/subEvents/create";
import SubEventDetail from "../pages/events/views/subEvents/detail";
import AnnouncementsList from "../pages/announcements/views/list";
import AnnouncementEdit from "../pages/announcements/views/edit";
import SubEventsList from "../pages/events/views/subEvents/list";
import SubEventEdit from "../pages/events/views/subEvents/edit";
import CompanyCreate from "../pages/companies/views/create";
import CompanyDetail from "../pages/companies/views/detail";
import CompaniesList from "../pages/companies/views/list";
import MemberDetail from "../pages/members/views/detail";
import CompanyEdit from "../pages/companies/views/edit";
import SettingsMain from "../pages/settings/views/main";
import EventCreate from "../pages/events/views/create";
import MembersList from "../pages/members/views/list";
import ProfileMain from "../pages/profile/views/main";
import ForgotPassword from "../pages/forgotPassword";
import FormCreate from "../pages/forms/views/create";
import FormDetail from "../pages/forms/views/detail";
import MemberEdit from "../pages/members/views/edit";
import syncSystemLocales from "../utils/syncLocales";
import EventsList from "../pages/events/views/list";
import NewsCreate from "../pages/news/views/create";
import NewsDetail from "../pages/news/views/detail";
import EventEdit from "../pages/events/views/edit";
import Notifications from "../pages/notifications";
import ResetPassword from "../pages/resetPassword";
import FormsList from "../pages/forms/views/list";
import FormEdit from "../pages/forms/views/edit";
import NewsEdit from "../pages/news/views/edit";
import NewsList from "../pages/news/views/list";
import Drive from "../pages/drive/views/list";
import packageJson from "../../package.json";
import logo from "../assets/images/logo.png";
import Dashboard from "../pages/dashboard";
import Requests from "../pages/requests";
import Terminal from "../pages/terminal";
import Content from "../pages/content";
import ThreeT from "../pages/threeT";
import Login from "../pages/login";

const AppStack = createNativeStackNavigator();
const RootStack = createNativeStackNavigator();

const RenderDashboardIcon = ({
    color
}: {
    color: keyof NCoreUIKit.IconContentColors;
}) => {
    const {
        colors
    } = NCoreUIKitTheme.useContext();

    return <HomeIcon
        color={colors.content.icon[color]}
        size={18}
    />;
};

const RenderMembersIcon = ({
    color
}: {
    color: keyof NCoreUIKit.IconContentColors;
}) => {
    const {
        colors
    } = NCoreUIKitTheme.useContext();

    return <UsersIcon
        color={colors.content.icon[color]}
        size={18}
    />;
};

const RenderThreeTIcon = ({
    color
}: {
    color: keyof NCoreUIKit.IconContentColors;
}) => {
    const {
        colors
    } = NCoreUIKitTheme.useContext();

    return <NetworkIcon
        color={colors.content.icon[color]}
        size={18}
    />;
};

const RenderTerminalIcon = ({
    color
}: {
    color: keyof NCoreUIKit.IconContentColors;
}) => {
    const {
        colors
    } = NCoreUIKitTheme.useContext();

    return <TerminalIcon
        color={colors.content.icon[color]}
        size={18}
    />;
};

const RenderEventsIcon = ({
    color
}: {
    color: keyof NCoreUIKit.IconContentColors;
}) => {
    const {
        colors
    } = NCoreUIKitTheme.useContext();

    return <CalendarDaysIcon
        color={colors.content.icon[color]}
        size={18}
    />;
};

const RenderCompaniesIcon = ({
    color
}: {
    color: keyof NCoreUIKit.IconContentColors;
}) => {
    const {
        colors
    } = NCoreUIKitTheme.useContext();

    return <Building2Icon
        color={colors.content.icon[color]}
        size={18}
    />;
};

const RenderFormsIcon = ({
    color
}: {
    color: keyof NCoreUIKit.IconContentColors;
}) => {
    const {
        colors
    } = NCoreUIKitTheme.useContext();

    return <ClipboardListIcon
        color={colors.content.icon[color]}
        size={18}
    />;
};

const RenderNewsIcon = ({
    color
}: {
    color: keyof NCoreUIKit.IconContentColors;
}) => {
    const {
        colors
    } = NCoreUIKitTheme.useContext();

    return <NewspaperIcon
        color={colors.content.icon[color]}
        size={18}
    />;
};

const RenderSettingsIcon = ({
    color
}: {
    color: keyof NCoreUIKit.IconContentColors;
}) => {
    const {
        colors
    } = NCoreUIKitTheme.useContext();

    return <SettingsIcon
        color={colors.content.icon[color]}
        size={18}
    />;
};

const RenderAnnouncementsIcon = ({
    color
}: {
    color: keyof NCoreUIKit.IconContentColors;
}) => {
    const {
        colors
    } = NCoreUIKitTheme.useContext();

    return <MegaphoneIcon
        color={colors.content.icon[color]}
        size={18}
    />;
};

const RenderNotificationsIcon = ({
    color
}: {
    color: keyof NCoreUIKit.IconContentColors;
}) => {
    const {
        colors
    } = NCoreUIKitTheme.useContext();

    return <BellIcon
        color={colors.content.icon[color]}
        size={18}
    />;
};

const RenderRequestsIcon = ({
    color
}: {
    color: keyof NCoreUIKit.IconContentColors;
}) => {
    const {
        colors
    } = NCoreUIKitTheme.useContext();

    return <InboxIcon
        color={colors.content.icon[color]}
        size={18}
    />;
};

const RenderDriveIcon = ({
    color
}: {
    color: keyof NCoreUIKit.IconContentColors;
}) => {
    const {
        colors
    } = NCoreUIKitTheme.useContext();

    return <HardDriveIcon
        color={colors.content.icon[color]}
        size={18}
    />;
};

const RenderContentIcon = ({
    color
}: {
    color: keyof NCoreUIKit.IconContentColors;
}) => {
    const {
        colors
    } = NCoreUIKitTheme.useContext();

    return <FileTextIcon
        color={colors.content.icon[color]}
        size={18}
    />;
};

const updateStorage = () => {
    if (Platform.OS === "web") {
        const data = NCoreUIKitEmbeddedMenu.state.data;

        if (data && data.length) {
            const currentMenu = data.find(dI => dI.id === "main-menu");

            if (currentMenu) {
                window.localStorage.setItem("main-menu-status", String(currentMenu.isCollapse));
            }
        }
    }
};

const updateLocaleStorage = () => {
    if (Platform.OS === "web") {
        const localeData = NCoreUIKitLocalize.state;

        if (localeData) {
            window.localStorage.setItem("locale", JSON.stringify(localeData));
        }
    }
};

const updateThemeStorage = () => {
    if (Platform.OS === "web") {
        const themeData = NCoreUIKitTheme.state;

        if (themeData) {
            window.localStorage.setItem("theme", JSON.stringify(themeData));
        }
    }
};

const RenderSiteLogo = ({
    isActive
}: {
    isActive: boolean;
}) => {
    const {
        localize
    } = NCoreUIKitLocalize.useContext();

    if (!isActive) {
        return null;
    }

    return <SiteLogo
        backgroundColor="transparent"
        title={localize("menu")}
        imageSpace="spacingLg"
        isShowBorder={false}
        isShowIcon={false}
        titleColor="mid"
    />;
};

const RenderVersion = () => {
    const {
        colors,
        spaces
    } = NCoreUIKitTheme.useContext();

    return <View
        style={[
            stylesheet.versionContainer,
            {
                padding: spaces.spacingMd
            }
        ]}
    >
        <Text
            customColor={colors.content.text.low}
            variant="labelSmallSize"
        >
            v{packageJson.version}
        </Text>
    </View>;
};

const RenderRightComponent = () => {
    const {
        isAuthenticated,
        logout,
        user
    } = useContext(AuthContext);

    const navigation = useNavigation<NativeStackNavigationProp<RootStackParamList>>();

    const {
        spaces,
        colors
    } = NCoreUIKitTheme.useContext();

    const {
        width
    } = useWindowDimensions();
    const isMobile = width < 768;

    return <View
        style={[
            stylesheet.rightContainer,
            {
                gap: spaces.spacingMd
            }
        ]}
    >
        {/*  <LocaleSwitcher
            spreadBehaviour="free"
            variant="outline"
        />
        <ThemeSwitcher
            spreadBehaviour="free"
            variant="outline"
        /> */}
        {
            !isMobile && (
                <UserShortcut
                    avatarProps={{
                        title: user?.fullName || user?.name_surname || user?.username || user?.firstName,
                        imageUrl: user?.profilePhoto || user?.profilePicturePath,
                        statusIndicatorType: "online",
                        isStatusIndicator: true,
                        size: "xSmall"
                    }}
                    title={user?.fullName || user?.name_surname || user?.username || user?.firstName}
                    onLoginPress={async () => {
                        if (isAuthenticated && user?.role === "guest") {
                            await AsyncStorage.setItem("redirect_to_login", "true");
                            await logout();
                        } else {
                            navigation.navigate("Login");
                        }
                    }}
                    actionButtons={
                        isAuthenticated && user?.role !== "guest" ? [
                            {
                                onPress: () => {
                                    setTimeout(() => {
                                        navigation.navigate("AuthenticatedApp", {
                                            screen: "ProfileMain"
                                        });
                                    }, 100);
                                },
                                customTextColor: colors.project.brandWhite,
                                customBorderColor: "secondaryButton",
                                icon: ({
                                    size
                                }) => <UserIcon
                                    color={colors.project.brandWhite}
                                    size={size}
                                />,
                                isCloseBottomSheetOnPress: true,
                                customColor: "secondaryButton",
                                variant: "outline",
                                title: "Profil"
                            }
                        ] : undefined
                    }
                    isSessionActive={isAuthenticated && user?.role !== "guest"}
                    onPress={() => {
                        if (isAuthenticated && user?.role !== "guest") {
                            navigation.navigate("AuthenticatedApp", {
                                screen: "ProfileMain"
                            });
                        }
                    }}
                    isShowLoginButtonWhenNoSession={true}
                    isWorkWithCoreComplex={false}
                    backgroundColor="transparent"
                    onLogoutPress={async () => {
                        await logout();
                    }}
                    variant="default"
                />)
        }
        {
            isMobile && (
                <Button
                    icon={({
                        color,
                        size
                    }) => <MenuIcon
                        color={colors.content.icon[color]}
                        size={size + 5}
                    />}
                    onPress={() => {
                        NCoreUIKitMenu.open({
                            id: "main-menu"
                        });
                    }}
                    spreadBehaviour="free"
                    type="neutral"
                />)
        }
    </View>;
};

type DeepRouteState = {
    routes?: Array<{
        state?: DeepRouteState;
        name: string;
    }>;
    index?: number;
};

const getDeepActiveRouteName = (
    state: DeepRouteState
): string | undefined => {
    if(!state?.routes?.length) {
        return undefined;
    }

    const activeIndex = state.index ?? 0;
    const activeRoute = state.routes[activeIndex];

    if(!activeRoute) {
        return undefined;
    }

    if(activeRoute.state) {
        return getDeepActiveRouteName(activeRoute.state);
    }

    return activeRoute.name;
};

const getDynamicMenuButtons = (
    navigation?: NCoreUIKit.Navigation,
    currentRoute?: string,
    userRole?: string
): MenuButton[] => {
    const dynamicButtons: MenuButton[] = [];

    if(userRole) {
        dynamicButtons.push({
            icon: ({
                color
            }: { color: keyof NCoreUIKit.IconContentColors }) => {
                return <RenderDashboardIcon
                    color={color}
                />;
            },
            title: "menu-dashboard-title",
            redirectMain: "Dashboard",
            isUseLocalize: true
        });

        const isMembersActive = currentRoute === "MembersList" || currentRoute === "MemberDetail" || currentRoute === "MemberEdit" || currentRoute === "MemberCreate";
        dynamicButtons.push({
            props: {
                onPress: () => {
                    if(isMembersActive && currentRoute !== "MembersList") {
                        navigation?.navigate("MembersList");
                    }
                }
            },
            redirectMain: isMembersActive ? currentRoute : "MembersList",
            icon: ({
                color
            }: { color: keyof NCoreUIKit.IconContentColors }) => {
                return <RenderMembersIcon
                    color={color}
                />;
            },
            title: "menu-members-title",
            isUseLocalize: true
        });

        const isEventsActive = currentRoute === "SubEventsList" || currentRoute === "SubEventCreate" || currentRoute === "SubEventDetail" || currentRoute === "SubEventEdit" || currentRoute === "EventCreate" || currentRoute === "EventsList" || currentRoute === "EventEdit";
        dynamicButtons.push({
            props: {
                onPress: () => {
                    if(isEventsActive && currentRoute !== "EventsList") {
                        navigation?.navigate("EventsList");
                    }
                }
            },
            redirectMain: isEventsActive ? currentRoute : "EventsList",
            icon: ({
                color
            }: { color: keyof NCoreUIKit.IconContentColors }) => {
                return <RenderEventsIcon
                    color={color}
                />;
            },
            title: "menu-events-title",
            isUseLocalize: true
        });

        const isFormsActive = currentRoute === "FormCreate" || currentRoute === "FormDetail" || currentRoute === "FormEdit" || currentRoute === "Forms";
        dynamicButtons.push({
            props: {
                onPress: () => {
                    if(isFormsActive && currentRoute !== "Forms") {
                        navigation?.navigate("Forms");
                    }
                }
            },
            icon: ({
                color
            }: { color: keyof NCoreUIKit.IconContentColors }) => {
                return <RenderFormsIcon
                    color={color}
                />;
            },
            redirectMain: isFormsActive ? currentRoute : "Forms",
            title: "menu-forms-title",
            isUseLocalize: true
        });

        const isCompaniesActive = currentRoute === "CompaniesList" || currentRoute === "CompanyCreate" || currentRoute === "CompanyDetail" || currentRoute === "CompanyEdit";
        dynamicButtons.push({
            props: {
                onPress: () => {
                    if(isCompaniesActive && currentRoute !== "CompaniesList") {
                        navigation?.navigate("CompaniesList");
                    }
                }
            },
            redirectMain: isCompaniesActive ? currentRoute : "CompaniesList",
            icon: ({
                color
            }: { color: keyof NCoreUIKit.IconContentColors }) => {
                return <RenderCompaniesIcon
                    color={color}
                />;
            },
            title: "menu-companies-title",
            isUseLocalize: true
        });

        dynamicButtons.push({
            icon: ({
                color
            }: { color: keyof NCoreUIKit.IconContentColors }) => {
                return <RenderThreeTIcon
                    color={color}
                />;
            },
            title: "menu-threet-title",
            redirectMain: "ThreeT",
            isUseLocalize: true
        });
    }

    const isNewsActive = currentRoute === "NewsList" || currentRoute === "NewsCreate" || currentRoute === "NewsDetail" || currentRoute === "NewsEdit";
    dynamicButtons.push({
        props: {
            onPress: () => {
                if(isNewsActive && currentRoute !== "NewsList") {
                    navigation?.navigate("NewsList");
                }
            }
        },
        redirectMain: isNewsActive ? currentRoute : "NewsList",
        icon: ({
            color
        }: { color: keyof NCoreUIKit.IconContentColors }) => {
            return <RenderNewsIcon
                color={color}
            />;
        },
        title: "menu-news-title",
        isUseLocalize: true
    });

    const isAnnouncementsActive = currentRoute === "AnnouncementsList" || currentRoute === "AnnouncementCreate" || currentRoute === "AnnouncementDetail" || currentRoute === "AnnouncementEdit";
    dynamicButtons.push({
        props: {
            onPress: () => {
                if(isAnnouncementsActive && currentRoute !== "AnnouncementsList") {
                    navigation?.navigate("AnnouncementsList");
                }
            }
        },
        redirectMain: isAnnouncementsActive ? currentRoute : "AnnouncementsList",
        icon: ({
            color
        }: { color: keyof NCoreUIKit.IconContentColors }) => {
            return <RenderAnnouncementsIcon
                color={color}
            />;
        },
        title: "menu-announcements-title",
        isUseLocalize: true
    });

    const isNotificationsActive = currentRoute === "Notifications";
    dynamicButtons.push({
        props: {
            onPress: () => {
                if(isNotificationsActive && currentRoute !== "Notifications") {
                    navigation?.navigate("Notifications");
                }
            }
        },
        redirectMain: isNotificationsActive ? currentRoute : "Notifications",
        icon: ({
            color
        }: { color: keyof NCoreUIKit.IconContentColors }) => {
            return <RenderNotificationsIcon
                color={color}
            />;
        },
        title: "menu-notifications-title",
        isUseLocalize: true
    });

    const isRequestsActive = currentRoute === "Requests";
    dynamicButtons.push({
        props: {
            onPress: () => {
                if(isRequestsActive && currentRoute !== "Requests") {
                    navigation?.navigate("Requests");
                }
            }
        },
        redirectMain: isRequestsActive ? currentRoute : "Requests",
        icon: ({
            color
        }: { color: keyof NCoreUIKit.IconContentColors }) => {
            return <RenderRequestsIcon
                color={color}
            />;
        },
        title: "menu-requests-title",
        isUseLocalize: true
    });

    const isDriveActive = currentRoute === "Drive";
    dynamicButtons.push({
        props: {
            onPress: () => {
                if(isDriveActive && currentRoute !== "Drive") {
                    navigation?.navigate("Drive");
                }
            }
        },
        icon: ({
            color
        }: { color: keyof NCoreUIKit.IconContentColors }) => {
            return <RenderDriveIcon
                color={color}
            />;
        },
        redirectMain: isDriveActive ? currentRoute : "Drive",
        title: "menu-drive-title",
        isUseLocalize: true
    });

    const isContentActive = currentRoute === "Content";
    dynamicButtons.push({
        props: {
            onPress: () => {
                if(isContentActive && currentRoute !== "Content") {
                    navigation?.navigate("Content");
                }
            }
        },
        redirectMain: isContentActive ? currentRoute : "Content",
        icon: ({
            color
        }: { color: keyof NCoreUIKit.IconContentColors }) => {
            return <RenderContentIcon
                color={color}
            />;
        },
        title: "menu-content-title",
        isUseLocalize: true
    });

    if (userRole) {
        const isSettingsActive = currentRoute === "SettingsMain" || currentRoute === "SettingsNewsCategories" || currentRoute === "SettingsSystemLocales";
        dynamicButtons.push({
            props: {
                onPress: () => {
                    if (isSettingsActive && currentRoute !== "SettingsMain") {
                        navigation?.navigate("SettingsMain");
                    }
                }
            },
            redirectMain: isSettingsActive ? currentRoute : "SettingsMain",
            icon: ({
                color
            }: { color: keyof NCoreUIKit.IconContentColors }) => {
                return <RenderSettingsIcon
                    color={color}
                />;
            },
            title: "menu-settings-title",
            isUseLocalize: true
        });
    }

    if (userRole === "root") {
        dynamicButtons.push({
            icon: ({
                color
            }: { color: keyof NCoreUIKit.IconContentColors }) => {
                return <RenderTerminalIcon
                    color={color}
                />;
            },
            redirectMain: "Terminal",
            isUseLocalize: false,
            title: "Terminal"
        });
    }

    return dynamicButtons;
};

const RootNav = () => {
    const {
        activeOrganizationFeatures,
        activeOrganizationId,
        isAuthenticated,
        loading,
        logout,
        user
    } = useContext(AuthContext);

    const {
        activeLocale,
        localize
    } = NCoreUIKitLocalize.useContext();

    const {
        width
    } = useWindowDimensions();
    const isMobile = width < 768;

    const navigation = useNavigation<NativeStackNavigationProp<RootStackParamList>>();

    const currentMenuStatus = Platform.OS === "web" ? window.localStorage.getItem("main-menu-status") : "false";

    const prevMenuDepsRef = useRef<string>("");

    const [
        currentRoute,
        setCurrentRoute
    ] = useState<string>("Dashboard");

    useEffect(() => {
        const unsubscribe = navigation.addListener("state", (e) => {
            if(e.data.state) {
                const routeName = getDeepActiveRouteName(e.data.state);

                if(routeName) {
                    setCurrentRoute(routeName);
                }
            }
        });

        return unsubscribe;
    }, [
        navigation
    ]);

    useEffect(() => {
        const userRole = user?.role;

        const menuDepsString = JSON.stringify({
            features: activeOrganizationFeatures,
            orgId: activeOrganizationId,
            currentRoute: currentRoute,
            status: currentMenuStatus,
            locale: activeLocale,
            isMobile: isMobile,
            user: user
        });

        if(prevMenuDepsRef.current === menuDepsString) {
            return;
        }

        prevMenuDepsRef.current = menuDepsString;

        const dynamicButtons = getDynamicMenuButtons(navigation as unknown as NCoreUIKit.Navigation, currentRoute, userRole);

        const currentMenuData = {
            navigation: navigation as unknown as NCoreUIKit.Navigation,
            renderFooter: RenderVersion as unknown as () => ReactNode,
            isCollapse: currentMenuStatus === "true" ? true : false,
            backgroundColor: "brandWhite" as const,
            renderSiteLogo: ((props?: {
                isActive?: boolean;
            }) => <RenderSiteLogo
                isActive={props?.isActive ?? true}
            />) as unknown as () => ReactNode,
            buttons: dynamicButtons as never[],
            isWorkWithSafeAreaView: false,
            isAutoClosed: true,
            id: "main-menu"
        };

        const existingEmbeddedMenu = NCoreUIKitEmbeddedMenu.get({
            id: "main-menu"
        });
        if (existingEmbeddedMenu) {
            NCoreUIKitEmbeddedMenu.update({
                menuData: currentMenuData,
                id: "main-menu"
            });
        } else {
            NCoreUIKitEmbeddedMenu.load(currentMenuData);
        }

        const existingMenu = NCoreUIKitMenu.get({
            id: "main-menu"
        });

        const mobileMenuFooter = () => <View>
            <UserShortcut
                avatarProps={{
                    title: user?.fullName || user?.name_surname || user?.username || user?.firstName,
                    imageUrl: user?.profilePhoto || user?.profilePicturePath,
                    statusIndicatorType: "online",
                    isStatusIndicator: true,
                    size: "xSmall"
                }}
                title={user?.fullName || user?.name_surname || user?.username || user?.firstName}
                onLoginPress={async () => {
                    NCoreUIKitMenu.close({
                        id: "main-menu"
                    });
                    if (isAuthenticated && user?.role === "guest") {
                        await AsyncStorage.setItem("redirect_to_login", "true");
                        await logout();
                    } else {
                        navigation.navigate("Login");
                    }
                }}
                isSessionActive={isAuthenticated && user?.role !== "guest"}
                onPress={() => {
                    if (isAuthenticated) {
                        NCoreUIKitMenu.close({
                            id: "main-menu"
                        });
                        navigation.navigate("AuthenticatedApp", {
                            screen: "ProfileMain"
                        });
                    }
                }}
                isShowLoginButtonWhenNoSession={true}
                isWorkWithCoreComplex={false}
                backgroundColor="transparent"
                onLogoutPress={async () => {
                    NCoreUIKitMenu.close({
                        id: "main-menu"
                    });
                    await logout();
                }}
                isShowLogoutButton={true}
                spreadBehaviour="stretch"
                isShowArrowButton={true}
                variant="drawer"
            />
            <RenderVersion />
        </View>;

        if (existingMenu) {
            NCoreUIKitMenu.update({
                menuData: {
                    ...currentMenuData,
                    renderFooter: mobileMenuFooter as unknown as () => ReactNode,
                    isCollapse: existingMenu.isCollapse,
                    isWorkWithSafeAreaView: true
                },
                id: "main-menu"
            });
        } else {
            NCoreUIKitMenu.load({
                ...currentMenuData,
                renderFooter: mobileMenuFooter as unknown as () => ReactNode,
                isWorkWithSafeAreaView: true,
                isCollapse: false
            });
        }
    }, [
        activeOrganizationFeatures,
        activeOrganizationId,
        currentMenuStatus,
        currentRoute,
        activeLocale,
        user?.role,
        localize,
        isMobile
    ]);

    useEffect(() => {
        NCoreUIKitLocalize.addEventListener("state", updateLocaleStorage);
        NCoreUIKitEmbeddedMenu.addEventListener("state", updateStorage);
        NCoreUIKitTheme.addEventListener("state", updateThemeStorage);

        return () => {
            NCoreUIKitLocalize.removeEventListener("state");
            NCoreUIKitEmbeddedMenu.removeEventListener("state");
            NCoreUIKitTheme.removeEventListener("state");
        };
    }, []);

    useEffect(() => {
        if(!isAuthenticated) {
            return;
        }

        syncSystemLocales(activeLocale);
    }, [
        isAuthenticated,
        activeLocale
    ]);

    const Pages = () => {
        return <Fragment>
            <RootStack.Screen
                component={CompanyCreate}
                name="CompanyCreate"
            />
            <RootStack.Screen
                component={CompanyDetail}
                name="CompanyDetail"
            />
            <RootStack.Screen
                component={CompanyEdit}
                name="CompanyEdit"
            />
            <RootStack.Screen
                component={CompaniesList}
                name="CompaniesList"
            />
            <RootStack.Screen
                component={Dashboard}
                name="Dashboard"
            />
            <RootStack.Screen
                component={Terminal}
                name="Terminal"
            />
            <RootStack.Screen
                component={MembersList}
                name="MembersList"
            />
            <RootStack.Screen
                component={MemberDetail}
                name="MemberDetail"
            />
            <RootStack.Screen
                component={MemberEdit}
                name="MemberEdit"
            />
            <RootStack.Screen
                component={SubEventsList}
                name="SubEventsList"
            />
            <RootStack.Screen
                component={SubEventCreate}
                name="SubEventCreate"
            />
            <RootStack.Screen
                component={SubEventDetail}
                name="SubEventDetail"
            />
            <RootStack.Screen
                component={SubEventEdit}
                name="SubEventEdit"
            />
            <RootStack.Screen
                component={EventsList}
                name="EventsList"
            />
            <RootStack.Screen
                component={EventCreate}
                name="EventCreate"
            />
            <RootStack.Screen
                component={EventEdit}
                name="EventEdit"
            />
            <RootStack.Screen
                component={FormsList}
                name="Forms"
            />
            <RootStack.Screen
                component={FormCreate}
                name="FormCreate"
            />
            <RootStack.Screen
                component={FormDetail}
                name="FormDetail"
            />
            <RootStack.Screen
                component={FormEdit}
                name="FormEdit"
            />
            <RootStack.Screen
                component={ThreeT}
                name="ThreeT"
            />
            <RootStack.Screen
                component={AnnouncementDetail}
                name="AnnouncementDetail"
            />
            <RootStack.Screen
                component={AnnouncementCreate}
                name="AnnouncementCreate"
            />
            <RootStack.Screen
                component={AnnouncementsList}
                name="AnnouncementsList"
            />
            <RootStack.Screen
                component={Notifications}
                name="Notifications"
            />
            <RootStack.Screen
                component={AnnouncementEdit}
                name="AnnouncementEdit"
            />
            <RootStack.Screen
                component={NewsDetail}
                name="NewsDetail"
            />
            <RootStack.Screen
                component={NewsCreate}
                name="NewsCreate"
            />
            <RootStack.Screen
                component={NewsEdit}
                name="NewsEdit"
            />
            <RootStack.Screen
                component={NewsList}
                name="NewsList"
            />
            <RootStack.Screen
                component={Drive}
                name="Drive"
            />
            <RootStack.Screen
                component={ProfileMain}
                name="ProfileMain"
            />
            <RootStack.Screen
                component={SettingsMain}
                name="SettingsMain"
            />
            <RootStack.Screen
                component={SettingsNewsCategories}
                name="SettingsNewsCategories"
            />
            <RootStack.Screen
                component={SettingsSystemLocales}
                name="SettingsSystemLocales"
            />
            <RootStack.Screen
                component={Requests}
                name="Requests"
            />
            <RootStack.Screen
                component={Content}
                name="Content"
            />
        </Fragment>;
    };

    if (loading) {
        return null;
    }

    if (!isAuthenticated) {
        return <AppStack.Navigator
            initialRouteName="Login"
            screenOptions={{
                headerShown: false
            }}
            key="unauth"
        >
            <AppStack.Screen
                component={Login}
                name="Login"
            />
            <AppStack.Screen
                component={ForgotPassword}
                name="ForgotPassword"
            />
            <AppStack.Screen
                component={ResetPassword}
                name="ResetPassword"
            />
        </AppStack.Navigator>;
    }

    return <AppStack.Navigator
        initialRouteName="AuthenticatedApp"
        screenOptions={{
            headerShown: false
        }}
        key="auth"
    >
        <AppStack.Screen
            name="AuthenticatedApp"
        >
            {() => <MainHeader
                siteLogoProps={{
                    onPress: () => {
                        const state = navigation.getState();

                        let isDashboard = false;

                        if (state) {
                    type RouteNode = {
                        state?: {
                            routes: RouteNode[];
                            index?: number;
                        };
                        name: string;
                    };

                    let currentRoute = state.routes[state.index] as RouteNode | undefined;

                    for(; currentRoute && currentRoute.state && typeof currentRoute.state.index === "number";) {
                        currentRoute = currentRoute.state.routes[currentRoute.state.index];
                    }

                    if (currentRoute && currentRoute.name === "Dashboard") {
                        isDashboard = true;
                    }
                        }

                        if (!isDashboard) {
                            navigation.navigate("AuthenticatedApp", {
                                screen: "Dashboard"
                            });
                        }
                    },
                    imageUrl: typeof logo === "string" ? logo : (Image.resolveAssetSource(logo)?.uri || ""),
                    title: localize("app-name"),
                    imageProps: {
                        resizeMode: "contain",
                        source: logo
                    },
                    imageSpace: "spacingMd",
                    isWorkWithAction: true,
                    isWorkWithFlex1: true
                }}
                customBackgroundColor="brandWhite"
                renderRight={RenderRightComponent}
                isWorkWithSeperator={true}
                isWorkWithSticky={true}
                glassEffect={5}
            >
                {
                    isMobile ?
                        <RootStack.Navigator
                            initialRouteName="Dashboard"
                            screenOptions={{
                                headerShown: false
                            }}
                        >
                            {Pages()}
                        </RootStack.Navigator>
                    :
                        <NCoreUIKitEmbeddedMenu.Render
                            navigation={navigation as unknown as NCoreUIKit.Navigation}
                            id="main-menu"
                        >
                            <RootStack.Navigator
                                initialRouteName="Dashboard"
                                screenOptions={{
                                    headerShown: false
                                }}
                            >
                                {Pages()}
                            </RootStack.Navigator>
                        </NCoreUIKitEmbeddedMenu.Render>
                }
            </MainHeader>
            }
        </AppStack.Screen>
    </AppStack.Navigator>;
};

const Navigation = () => {
    const {
        localize
    } = NCoreUIKitLocalize.useContext();

    const {
        isAuthenticated
    } = useContext(AuthContext);

    const getTitle = (routeName: string | undefined) => {
        switch (routeName) {
            case "ForgotPassword":
                return localize("forgot-password-page-title");
            case "ResetPassword":
                return localize("reset-password-page-title");
            case "SubEventCreate":
                return localize("event-detail-sub-events");
            case "SubEventDetail":
                return localize("event-detail-sub-events");
            case "SubEventEdit":
                return localize("event-detail-sub-events");
            case "SubEventsList":
                return localize("event-detail-sub-events");
            case "EventCreate":
                return localize("events-create-page-title");
            case "EventEdit":
                return localize("events-edit-page-title");
            case "EventsList":
                return localize("events-page-title");
            case "MembersList":
                return localize("menu-members-title");
            case "MemberCreate":
                return localize("members-create-new");
            case "MemberDetail":
                return localize("members-detail-title");
            case "MemberEdit":
                return localize("members-edit-title");
            case "Forms":
                return localize("menu-forms-title");
            case "FormCreate":
                return localize("forms-create-title");
            case "FormDetail":
                return localize("unknown");
            case "FormEdit":
                return localize("forms-edit-title");
            case "CompaniesList":
                return localize("companies-page-title");
            case "CompanyDetail":
                return localize("companies-detail-title");
            case "CompanyCreate":
                return localize("companies-create-title");
            case "CompanyEdit":
                return localize("companies-edit-title");
            case "Dashboard":
                return localize("dashboard-page-title");
            case "SettingsMain":
                return localize("menu-settings-title");
            case "SettingsNewsCategories":
                return localize("menu-settings-news-categories");
            case "SettingsSystemLocales":
                return localize("menu-settings-system-locales");

            case "AnnouncementDetail":
                return localize("announcement-detail-title");
            case "AnnouncementCreate":
                return localize("announcement-create-title");
            case "AnnouncementsList":
                return localize("menu-announcements-title");
            case "Notifications":
                return localize("menu-notifications-title");
            case "AnnouncementEdit":
                return localize("announcement-edit-title");
            case "NewsDetail":
                return localize("news-detail-title");
            case "NewsCreate":
                return localize("news-create-title");
            case "NewsEdit":
                return localize("news-edit-title");
            case "NewsList":
                return localize("menu-news-title");
            case "Drive":
                return localize("menu-drive-title");
            case "Requests":
                return localize("requests-page-title");
            case "Content":
                return localize("menu-content-title");
            case "Login":
                return localize("login-page-title");
            default:
                return localize("app-name");
        }
    };

    return <NavigationContainer
        linking={{
            config: {
                screens: {
                    AuthenticatedApp: {
                        screens: {
                            SubEventDetail: "events/sub-events/detail/:eventID/:subEventID",
                            SubEventEdit: "events/sub-events/edit/:eventID/:subEventID",
                            AnnouncementDetail: "announcements/detail/:announcementID",
                            AnnouncementEdit: "announcements/edit/:announcementID",
                            SubEventCreate: "events/sub-events/create/:eventID",
                            CompanyDetail: "companies/detail/:companyID",
                            SubEventsList: "events/sub-events/:eventID",
                            AnnouncementCreate: "announcements/create",
                            CompanyEdit: "companies/edit/:companyID",
                            MemberDetail: "members/detail/:userID",
                            AnnouncementsList: "announcements",
                            MemberEdit: "members/edit/:userID",
                            CompanyCreate: "companies/create",
                            EventEdit: "events/edit/:eventID",
                            NewsDetail: "news/detail/:newsID",
                            FormEdit: "forms/edit/:formID",
                            MemberCreate: "members/create",
                            Notifications: "notifications",
                            NewsEdit: "news/edit/:newsID",
                            EventCreate: "events/create",
                            CompaniesList: "companies",
                            FormCreate: "forms/create",
                            NewsCreate: "news/create",
                            Dashboard: "dashboard",
                            MembersList: "members",
                            ProfileMain: "profile",
                            EventsList: "events",
                            Requests: "requests",
                            Content: "content",
                            NewsList: "news",
                            Drive: "drive",
                            Forms: "forms"
                        }
                    },
                    ForgotPassword: "forgot-password",
                    ResetPassword: "reset-password",
                    Login: ""
                },
                initialRouteName: "Login"
            },
            getStateFromPath: (path, options) => {
                return getStateFromPath(path.toLowerCase(), options);
            },
            prefixes: [
                "http://localhost:3000",
                "https://gmcore-konya-admin-panel.app"
            ]
        } as LinkingOptions<RootStackParamList>}
        documentTitle={{
            formatter: (options, route) => {
                const title = options?.title ?? getTitle(route?.name);
                const appName = localize("app-name");

                if(title === appName) {
                    return title;
                }

                return `${title} | ${appName}`;
            }
        }}
        key={isAuthenticated ? "auth" : "unauth"}
    >
        <RootNav />
    </NavigationContainer>;
};
export default Navigation;
